import {
  getCreateReferenceId,
  getDurationInDays,
  isValidReference,
} from '@quromedical/fhir-common';
import type {
  AdmissionGetResponse,
  AdmissionGetResponseWithVitals,
  VitalsLiveDataPayload,
  WebsocketAlert,
} from '@quromedical/models';
import { orderby } from '@quromedical/utils';
import { getNameAgeAndGender } from 'core/display';
import {
  getMonitoringConfiguration,
  getOxygenSaturationScale,
  removeStaleMeasurements,
} from 'core/vitals';
import { router } from 'expo-router';
import { useUserSession } from 'hooks';
import { useActiveAssignedAdmissions } from 'hooks/useActiveAssignedAdmissions';
import { useLiveData } from 'integration/live-vitals';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import type { DashboardGridData, Layout, SearchData } from './sections';

type PatientAdmission = AdmissionGetResponse &
  Required<Pick<AdmissionGetResponse, 'patient'>>;

const isValidAdmission = (
  admission: AdmissionGetResponse,
): admission is PatientAdmission => isValidReference(admission.patient);

const getDashboardCardProps = (
  liveData: VitalsLiveDataPayload,
  alerts: Record<string, WebsocketAlert[]>,
  admission: PatientAdmission,
): DashboardGridData => {
  const patientId = getCreateReferenceId(admission.patient);
  const patientData = liveData[patientId];

  // if the user is on oxygen then we use Scale 2 Oxygen - otherwise we default to Scale 1
  // we currently don't account for Scale 2 Air which is meant to be used under special instruction
  // cases only
  const oxygenSaturationScale = getOxygenSaturationScale(admission.oxygen);

  const title = getNameAgeAndGender(admission.subject?.general);

  const baseProps: DashboardGridData = {
    title,
    patientId,
    admission,
    oxygenSaturationScale,
    alerts: alerts[patientId],
    serviceType: admission.service,
    servicePeriod: admission.period,
  };

  if (!patientData) {
    return baseProps;
  }

  const { ecg, latest } = patientData;

  const filteredLatest = removeStaleMeasurements(latest, Date.now());

  const hasLatest = !!Object.keys(filteredLatest).length;
  const hasEcg = !!ecg.length;
  const hasData = hasLatest || hasEcg;

  if (!hasData) {
    return baseProps;
  }

  return {
    ...baseProps,
    liveData: {
      ecg,
      latest: filteredLatest,
    },
  };
};

const orderByDataPoints = (filteredPropList: DashboardGridData[]) =>
  orderby(
    filteredPropList,
    (props) => Object.keys(props.liveData?.latest || {}).length,
    'desc',
  );

const searchPatients = (
  search: SearchData,
  admissions: PatientAdmission[] = [],
): PatientAdmission[] => {
  const cleanSearch = (search.search || '').trim().toLocaleLowerCase();

  const filteredAdmissions = admissions
    .filter((admission) => {
      if (!search.filter) {
        return true;
      }

      return admission.service === search.filter;
    })
    .filter((admission) => {
      if (!search.monitoringConfiguration) {
        return true;
      }

      const monitoringLevel =
        admission.service &&
        getMonitoringConfiguration(
          admission.service,
          getDurationInDays(admission.period?.start),
        );

      if (!monitoringLevel) {
        return true;
      }

      return monitoringLevel === search.monitoringConfiguration;
    });

  if (!cleanSearch) {
    return filteredAdmissions;
  }

  return filteredAdmissions.filter((admission) =>
    admission.patient?.display?.toLowerCase()?.includes(cleanSearch),
  );
};

interface DashboardData {
  search: SearchData;
  setSearch: (search: SearchData) => void;
  onLayoutChange: (layout: Layout) => void;
  liveData: VitalsLiveDataPayload;
  updated: number;

  patients: AdmissionGetResponseWithVitals[];
  error: boolean;
  isLoading: boolean;
  data: DashboardGridData[];
}

const DashboardContext = createContext<DashboardData>({
  search: {},
  setSearch: () => undefined,
  onLayoutChange: () => undefined,
  liveData: {},
  updated: 0,
  patients: [],
  error: false,
  isLoading: false,
  data: [],
});

export const DashboardContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [search, setSearch] = useState<SearchData>({});

  const onLayoutChange = useCallback((value: Layout) => {
    const screen = value === 'table' ? 'list' : 'grid';
    router.push({
      pathname: `/dashboard/${screen}`,
    });
  }, []);

  const { vitals: liveData, updated, alerts } = useLiveData();
  const session = useUserSession();

  const {
    data: patients = [],
    error,
    isLoading,
  } = useActiveAssignedAdmissions({
    locationId: session.ward,
  });

  const getProps = getDashboardCardProps.bind(undefined, liveData, alerts);

  // view dashboard cards for all active patients, even if we don't have fresh data for them
  const uniquePatients = patients.filter(isValidAdmission);
  const searchResults = searchPatients(search, uniquePatients);
  const filteredPropList = searchResults.map(getProps);

  const data = orderByDataPoints(filteredPropList);

  const value: DashboardData = {
    search,
    setSearch,
    onLayoutChange,
    liveData,
    updated,
    patients,
    error: !!error,
    isLoading,
    data,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => useContext(DashboardContext);
